import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row, Table, Typography, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FormInstance } from 'antd/lib/form';
import { useWatch } from 'rc-field-form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { selectUser } from '../../../auth/states/userSlice';
import { Fixed2 } from '../../../common/utils/common.utils';
import { FormButton } from '../../../components/common/FormItem/FormItems';
import { useGetAllAgentProfileQuery } from '../../Client/Agents_Profile/api/endpoints/agentProfileEndPoints';
import { useCheckCreditLimitMutation } from '../../Client/Client/api/endpoints/clientEndpoints';
import { useGetAllAirportQuery } from '../../Configuration/airport/endpoints/airportEndpoints';
import { nonCommitionInvoicePostType } from '../../Invoice_Non_Comission/types/nonInvoiceAirTicketTypes';
import { IReissueFormSubmit } from '../Type/InoviceReissue.interface';
import { useUpdateInvoiceReIssueAirticketMutation } from '../api/endpoints/reIssueAirTicketInvoiceEndpoints';
import { getReIssuePostFormatData } from '../utils/reissueFormatDataForPost';
import InvoiceReIssueSubTotal from './InvoiceReIssueSubTotal';
import { getAgentCommissionRate } from '../../IATA_AIR_TICKET/utils/ts/getAgentCommissionRate';
import InvPostIsDiscountIsPrevDue from '../../IATA_AIR_TICKET/Components/InvPostIsDiscountIsPrevDue';

type Props = {
  invoices: IReissueFormSubmit[];
  setInvoices: React.Dispatch<React.SetStateAction<IReissueFormSubmit[]>>;
  form: FormInstance<any>;
  setEditIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  editIndex?: number;
  edit_data?: nonCommitionInvoicePostType;
};

export default function EditverviewReIssue({
  invoices,
  setInvoices,
  form,
  setEditIndex,
  editIndex,
  edit_data,
}: Props) {
  // @STATES
  const [billingForm] = Form.useForm();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { reIssueEditId } = useParams();
  const [updateAndPrev, setUpdateAndPrev] = useState<boolean>(false);

  // @POST API
  const [
    editInvoiceReIssue,
    { isError, isLoading, isSuccess, data: postIdData },
  ] = useUpdateInvoiceReIssueAirticketMutation();

  const [checkCreditLimit, { isLoading: limiLoading }] =
    useCheckCreditLimitMutation();

  // @AIRPORT DATA
  const { data: airport } = useGetAllAirportQuery();
  const selectAirport = airport?.data;

  // // @VARIABLES
  const client_price = invoices.reduce(
    (a, b) => a + Fixed2(b?.airticket_client_price),
    0
  );

  const purchase_price = invoices.reduce(
    (a, b) => a + Fixed2(b?.airticket_purchase_price),
    0
  );
  const profit = invoices.reduce((a, b) => a + Fixed2(b?.airticket_profit), 0);

  // // @HANDLE COPY
  const handleCopy = (index: number) => {
    form.setFieldsValue(invoices[index]);
    setEditIndex(undefined);
    message.info('Copyed');
  };

  // // @HANDLE EDIT
  const handleEdit = (index: number) => {
    form.setFieldsValue(invoices[index]);
    setEditIndex(index);
  };

  // // @HANDLE DELETE
  const handleDelete = (index: number) => {
    setInvoices(invoices.filter((_, i) => i !== index));
    setEditIndex(undefined);
  };

  // @TABLE COLUMNS
  const column: ColumnsType<any> = [
    {
      width: 15,
      title: 'SL',
      render(_, __, index) {
        return <>{index + 1}</>;
      },
    },
    {
      title: 'Ticket No',
      dataIndex: 'airticket_ticket_no',
      key: 'airticket_ticket_no',
    },
    {
      title: 'Sales Price',
      dataIndex: 'airticket_client_price',
      key: 'airticket_client_price',
      render(value) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Purchase Price',
      dataIndex: 'airticket_purchase_price',
      key: 'airticket_purchase_price',
      render(value) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Profit',
      dataIndex: 'airticket_profit',
      key: 'airticket_profit',
      render(value) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Action',
      render(_, obj, index) {
        return (
          <span>
            <Button
              title={'Copy'}
              onClick={() => handleCopy(index)}
              size='small'
              type='text'
            >
              <CopyOutlined />
            </Button>
            <Button
              title={'Edit'}
              onClick={() => handleEdit(index)}
              size='small'
              type='text'
            >
              <EditOutlined />
            </Button>
            <Button
              title={'Delete'}
              onClick={() => handleDelete(index)}
              size='small'
              type='text'
            >
              <DeleteOutlined />
            </Button>
          </span>
        );
      },
    },
  ];

  // @HANDLE SUBMIT
  const handleSubmit = (data: any) => {
    const formatedData = getReIssuePostFormatData({
      invoices,
      billing_form: billingForm,
      user_id: user?.user_id as number,
      selectAirport,
    });

    if (reIssueEditId) {
      const prevNetTotal = 0;
      const currentNetTotal = 5000;
      if (prevNetTotal >= currentNetTotal) {
        editInvoiceReIssue({ body: formatedData, id: reIssueEditId });
      } else {
        checkCreditLimit({
          amount: currentNetTotal - prevNetTotal,
          combClient: formatedData.invoice_info.invoice_combclient_id,
        }).then((res: any) => {
          if (res?.data?.data === 1) {
            editInvoiceReIssue({ body: formatedData, id: reIssueEditId });
          } else {
            message.error(res?.data?.data);
          }
        });
      }
    }
  };

  // // @AFTER CREATE SHOW ALERT
  useEffect(() => {
    if (isSuccess && postIdData?.data) {
      message.success('Invoice Updated Successfully');

      updateAndPrev
        ? navigate(`/reissueairticket/details/${reIssueEditId}`, {
            state: '/reissueairticket',
          })
        : navigate('/reissueairticket');
    }
  }, [isSuccess, postIdData]);

  // @SHOW ERROR
  useEffect(() => {
    if (isError) message.error('Something went wrong, please try again!');
  }, [isError]);

  // GET ALL AGENT
  const { data: agentData } = useGetAllAgentProfileQuery();
  const getAllAgentProfile = agentData?.data;

  useEffect(() => {
    billingForm.setFieldValue('invoice_show_passport_details', 0);

    // @HANDLE AGENT COMISSION
    if (invoices[invoices?.length - 1]?.invoice_agent_id) {
      const agentCom =
        getAgentCommissionRate({
          allAgents: getAllAgentProfile,
          invoice_agent_id: invoices[invoices?.length - 1]?.invoice_agent_id,
        }) || 0;
      const commission = Fixed2((agentCom / 100) * (client_price || 0));

      billingForm.setFieldValue('invoice_agent_com_amount', commission);
    } else {
      billingForm.setFieldValue('invoice_agent_com_amount', undefined);
    }
  }, [invoices]);

  // @EDIT TIME FILLUP DATA
  useEffect(() => {
    const billingData = edit_data?.invoice_info;

    billingForm.setFieldsValue({
      invoice_discount: Fixed2(billingData?.invoice_discount),
      invoice_service_charge: Fixed2(billingData?.invoice_service_charge),
      invoice_vat: Fixed2(billingData?.invoice_vat),
      invoice_agent_com_amount: Number(
        billingData?.invoice_agent_com_amount || 0
      ),
      invoice_reference: billingData?.invoice_reference,
      invoice_note: billingData?.invoice_note,
      invoice_show_prev_due: billingData?.invoice_show_prev_due,
      invoice_show_discount: billingData?.invoice_show_discount,
    });
  }, [edit_data, invoices]);

  const invoice_sub_total = useWatch(['invoice_sub_total'], billingForm);
  const discount: number = useWatch(['invoice_discount'], billingForm);
  const serviceCharge: number = useWatch(
    ['invoice_service_charge'],
    billingForm
  );
  const vat: number = useWatch(['invoice_vat'], billingForm);

  const netTotal = Fixed2(
    Fixed2(client_price || 0) -
      Fixed2(discount || 0) +
      Fixed2(vat || 0) +
      (Fixed2(serviceCharge) || 0)
  );

  useEffect(() => {
    billingForm.setFieldsValue({
      invoice_net_total: Fixed2(netTotal),
    });
  }, [client_price]);

  return (
    <Row gutter={[10, 0]}>
      <Table
        style={{ width: '100%' }}
        size='small'
        columns={column}
        dataSource={invoices}
        pagination={false}
        bordered
        rowClassName={(record, index) =>
          index === editIndex ? 'record-edit-active' : ''
        }
        summary={(_) => {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={2} index={0}>
                <Typography style={{ textAlign: 'right' }}>Total : </Typography>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3}>
                <Typography.Text style={{ textAlign: 'right' }}>
                  {Fixed2(client_price)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align='right'>
                <Typography.Text style={{ textAlign: 'right' }}>
                  {Fixed2(purchase_price)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <Typography.Text style={{ textAlign: 'right' }}>
                  {Fixed2(profit)}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}></Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />

      <Form
        form={billingForm}
        onFinish={handleSubmit}
        layout='vertical'
        style={{ marginTop: '50px' }}
        disabled={!invoices?.length}
      >
        <InvoiceReIssueSubTotal
          billingForm={billingForm}
          isAgentComission={!invoices[invoices?.length - 1]?.invoice_agent_id}
          client_price={client_price}
        />
        <InvPostIsDiscountIsPrevDue billingForm={billingForm} />

        {/* @AIRTICKET POST BUTTON */}
        <Row gutter={[10, 10]}>
          <Col>
            <FormButton
              loading={!updateAndPrev && (isLoading || limiLoading)}
              disabled={!invoices?.length}
              label='UPDATE'
              onClick={() => {
                setUpdateAndPrev(false);
              }}
            />
          </Col>
          <Col>
            <FormButton
              loading={updateAndPrev && (isLoading || limiLoading)}
              disabled={!invoices?.length}
              label='UPDATE & VIEW'
              onClick={() => {
                setUpdateAndPrev(true);
              }}
            />
          </Col>
        </Row>
      </Form>
    </Row>
  );
}
